import React, { useState, useEffect, useRef } from "react";
import {
  Globe,
  Trash2,
  ChevronDown,
  AlertTriangle,
  Loader2,
  CheckCircle,
  Plus,
  Check,
  Eye,
  EyeOff,
  Package,
  Send,
  FileJson,
  Link2,
  GripVertical,
  Code,
  Braces,
} from "lucide-react";
import { WorkflowNodeModal } from "../../../common/shared/WorkflowNodeModal";
import syntaxHighlight from "../../../utils/jsonViewer";


// Method color configurations for n8n-style theming
export const methodColors: Record<string, { bg: string; text: string; accent: string; light: string; border: string }> = {
  GET: { bg: "bg-emerald-500", text: "text-emerald-700", accent: "bg-emerald-500", light: "bg-emerald-50", border: "border-emerald-200" },
  POST: { bg: "bg-amber-500", text: "text-amber-700", accent: "bg-amber-500", light: "bg-amber-50", border: "border-amber-200" },
  PUT: { bg: "bg-blue-500", text: "text-blue-700", accent: "bg-blue-500", light: "bg-blue-50", border: "border-blue-200" },
  DELETE: { bg: "bg-rose-500", text: "text-rose-700", accent: "bg-rose-500", light: "bg-rose-50", border: "border-rose-200" },
  PATCH: { bg: "bg-violet-500", text: "text-violet-700", accent: "bg-violet-500", light: "bg-violet-50", border: "border-violet-200" },
};

export interface KeyValueParam {
  id: string;
  key: string;
  value: string;
}

// Custom Select Component
interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  placeholder = "Select...",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as any)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedLabel = options.find((opt) => opt.value === value)?.label;

  return (
    <div className="relative" ref={containerRef}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full pl-3 pr-9 py-2 bg-white border-2 ${
          isOpen ? "border-gray-900 shadow-sm" : "border-gray-200 hover:border-gray-300"
        } rounded-lg text-sm text-gray-800 cursor-pointer transition-all flex items-center justify-between select-none`}
      >
        <span className={`truncate ${!value ? "text-gray-400" : "font-semibold"}`}>
          {selectedLabel || placeholder}
        </span>
        <ChevronDown
          className={`absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto animate-fadeIn">
          <div className="p-1.5">
            {options.map((option) => (
              <div
                key={option.value}
                onClick={() => {
                  onChange(option.value);
                  setIsOpen(false);
                }}
                className={`px-3 py-2 rounded-lg text-sm cursor-pointer transition-all flex items-center justify-between ${
                  value === option.value
                    ? "bg-gray-900 text-white font-medium"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                {option.label}
                {value === option.value && <Check className="w-4 h-4" />}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

interface HttpApiConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  nodeId: string;
  // Configuration state
  method: string;
  setMethod: (value: string) => void;
  url: string;
  setUrl: (value: string) => void;
  authType: string;
  setAuthType: (value: string) => void;
  authToken: string;
  setAuthToken: (value: string) => void;
  // Query params
  sendQuery: boolean;
  setSendQuery: (value: boolean) => void;
  queryParams: KeyValueParam[];
  setQueryParams: React.Dispatch<React.SetStateAction<KeyValueParam[]>>;
  // Headers
  sendHeaders: boolean;
  setSendHeaders: (value: boolean) => void;
  headers: KeyValueParam[];
  setHeaders: React.Dispatch<React.SetStateAction<KeyValueParam[]>>;
  // Body
  sendBody: boolean;
  setSendBody: (value: boolean) => void;
  isJsonBody: boolean;
  setIsJsonBody: (value: boolean) => void;
  jsonBody: string;
  setJsonBody: (value: string) => void;
  bodyParams: KeyValueParam[];
  setBodyParams: React.Dispatch<React.SetStateAction<KeyValueParam[]>>;
  // Execution
  executionStatus: string | null;
  error?: string;
  outputs?: Record<string, any>;
  // Validation
  validationStatus: { isValid: boolean; errors: string[] };
  // Param helpers
  addParam: (setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => void;
  removeParam: (id: string, setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => void;
  updateParam: (id: string, field: "key" | "value", newValue: string, setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => void;
}

export const HttpApiConfigModal: React.FC<HttpApiConfigModalProps> = ({
  isOpen,
  onClose,
  nodeId,
  method,
  setMethod,
  url,
  setUrl,
  authType,
  setAuthType,
  authToken,
  setAuthToken,
  sendQuery,
  setSendQuery,
  queryParams,
  setQueryParams,
  sendHeaders,
  setSendHeaders,
  headers,
  setHeaders,
  sendBody,
  setSendBody,
  isJsonBody,
  setIsJsonBody,
  jsonBody,
  setJsonBody,
  bodyParams,
  setBodyParams,
  executionStatus,
  error,
  outputs,
  validationStatus,
  addParam,
  removeParam,
  updateParam,
}) => {
  const [showToken, setShowToken] = useState(false);
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const isExecuting = executionStatus === "running";
  const executionSuccess = executionStatus === "success";
  const executionError = executionStatus === "error";

  const normalizedOutput =
    outputs &&
    typeof outputs === "object" &&
    !Array.isArray(outputs) &&
    Object.keys(outputs).length === 1 &&
    Object.prototype.hasOwnProperty.call(outputs, "outputs")
      ? (outputs as Record<string, any>).outputs
      : outputs;
  const hasOutputData = normalizedOutput !== undefined && normalizedOutput !== null;

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
  };

  const handleDrop = (
    e: React.DragEvent,
    setter: (value: string) => void,
    currentValue: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingOver(false);
    const variableString = e.dataTransfer.getData("text/plain");
    if (variableString) {
      const input = e.target as HTMLInputElement;
      const startPos = input.selectionStart || currentValue.length;
      const endPos = input.selectionEnd || currentValue.length;
      const newValue =
        currentValue.substring(0, startPos) +
        variableString +
        currentValue.substring(endPos);
      setter(newValue);
    }
  };

  return (
    <WorkflowNodeModal
      isOpen={isOpen}
      onClose={onClose}
      nodeId={nodeId}
      title="HTTP Request - API call"
      icon={<Globe className="w-5 h-5 text-indigo-600" />}
      themeColor="indigo"
      nodeDocsType="HTTP-API"
      validationStatus={validationStatus}
      nodeData={{ outputs }}
      nodeIcon={<Globe className="w-6 h-6 text-gray-400" />}
      emptyOutputsText="No output data"
      renderOutputsSidebar={() => (
        <div className="w-[430px] bg-white border-l border-gray-200 flex flex-col flex-shrink-0">
          <div className="px-5 py-4 border-b border-gray-200 bg-gradient-to-b from-gray-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md border border-gray-200 bg-white flex items-center justify-center">
                  <Package className="w-4 h-4 text-gray-600" />
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">Output</h3>
                  <p className="text-[11px] text-gray-500">Run Data</p>
                </div>
              </div>
              <span className="text-[10px] font-semibold px-2 py-1 rounded-md border border-gray-200 bg-white text-gray-500">
                JSON
              </span>
            </div>
          </div>

          <div className="px-4 pt-4">
            <div className={`rounded-lg border p-3 ${
              executionSuccess ? "border-emerald-200 bg-emerald-50" :
              executionError ? "border-red-200 bg-red-50" :
              isExecuting ? "border-blue-200 bg-blue-50" :
              "border-gray-200 bg-gray-50"
            }`}>
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">Status</span>
                <div className={`flex items-center gap-1.5 text-[10px] font-semibold px-2 py-1 rounded ${
                  executionSuccess ? "bg-emerald-100 text-emerald-700" :
                  executionError ? "bg-red-100 text-red-700" :
                  isExecuting ? "bg-blue-100 text-blue-700" :
                  "bg-white text-gray-600 border border-gray-200"
                }`}>
                  {isExecuting && <Loader2 className="w-3 h-3 animate-spin" />}
                  {executionSuccess && <CheckCircle className="w-3 h-3" />}
                  {executionError && <AlertTriangle className="w-3 h-3" />}
                  {executionStatus?.toUpperCase() || "READY"}
                </div>
              </div>
              {executionError && error && (
                <div className="mt-2 text-xs text-red-700">
                  {error}
                </div>
              )}
            </div>
          </div>

          <div className="flex-1 min-h-0 p-4 pt-3 flex flex-col gap-3">
            {hasOutputData ? (
              <>
                <div className="flex-1 min-h-0 bg-gray-50 border border-gray-200 rounded-lg overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:bg-gray-200 [&::-webkit-scrollbar-thumb]:rounded-full hover:[&::-webkit-scrollbar-thumb]:bg-gray-300">
                  <pre
                    className="p-3 text-xs font-mono break-all whitespace-pre-wrap text-gray-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: syntaxHighlight(normalizedOutput) }}
                  />
                </div>
              </>
            ) : (
              <div className="flex-1 min-h-0 bg-gray-50 border border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                <div className="text-center px-6">
                  <div className="w-10 h-10 rounded-md border border-gray-200 bg-white flex items-center justify-center mx-auto mb-3">
                    <Package className="w-5 h-5 text-gray-400" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">No output data</p>
                  <p className="text-xs text-gray-400 mt-1">Run the request to load response JSON.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    >
      {/* Center config content */}
      <div className="max-w-4xl mx-auto">
        <div className="rounded-xl border border-gray-200 bg-white p-5 space-y-4">
          <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
            <CustomSelect
              value={method}
              onChange={setMethod}
              options={[
                { label: "GET", value: "GET" },
                { label: "POST", value: "POST" },
                { label: "PUT", value: "PUT" },
                { label: "DELETE", value: "DELETE" },
                { label: "PATCH", value: "PATCH" },
              ]}
            />
            <div
              onDrop={(e) => handleDrop(e, setUrl, url)}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`relative transition-all rounded-lg ${isDraggingOver ? "ring-2 ring-gray-900" : ""}`}
            >
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://api.example.com/endpoint"
                className="w-full px-3 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm text-gray-800 focus:border-gray-900 focus:ring-0 outline-none transition font-mono placeholder:text-gray-400"
              />
              {isDraggingOver && (
                <div className="absolute inset-0 bg-gray-900/10 flex items-center justify-center rounded-lg pointer-events-none border-2 border-dashed border-gray-900">
                  <span className="text-xs text-gray-700 font-semibold">Drop variable</span>
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-[120px_minmax(0,1fr)] gap-3">
            <CustomSelect
              value={authType}
              onChange={setAuthType}
              options={[
                { label: "No Auth", value: "none" },
                { label: "Bearer", value: "bearer" },
              ]}
            />

            {authType === "bearer" ? (
              <div
                onDrop={(e) => handleDrop(e, setAuthToken, authToken)}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className="relative animate-fadeIn"
              >
                <form autoComplete="off" onSubmit={(e) => e.preventDefault()} className="relative w-full">
                  <input
                    type={showToken ? "text" : "password"}
                    value={authToken}
                    onChange={(e) => setAuthToken(e.target.value)}
                    placeholder="Bearer token"
                    autoComplete="new-password"
                    name={`token-${nodeId}`}
                    className="w-full pl-3 pr-10 py-2 bg-white border-2 border-gray-200 rounded-lg text-sm text-gray-800 focus:border-gray-900 focus:ring-0 outline-none transition font-mono"
                  />
                </form>
                <button
                  type="button"
                  onClick={() => setShowToken(!showToken)}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                >
                  {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            ) : (
              <div className="h-[42px] px-3 rounded-lg border-2 border-gray-200 bg-gray-50 flex items-center text-xs text-gray-400">
                No authentication
              </div>
            )}
          </div>

          <div className="border-t border-gray-100 pt-4 space-y-3">
            <CollapsibleSection
              title="Query Params"
              icon={<Link2 className={`w-4 h-4 ${sendQuery ? "text-violet-600" : "text-gray-500"}`} />}
              iconBg={sendQuery ? "bg-violet-100" : "bg-gray-200"}
              enabled={sendQuery}
              onToggle={() => setSendQuery(!sendQuery)}
            >
              <KeyValueList
                items={queryParams}
                setItems={setQueryParams}
                addParam={addParam}
                removeParam={removeParam}
                updateParam={updateParam}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                keyPlaceholder="key"
                valuePlaceholder="value"
                addButtonText="Add Parameter"
              />
            </CollapsibleSection>

            <CollapsibleSection
              title="Headers"
              icon={<FileJson className={`w-4 h-4 ${sendHeaders ? "text-blue-600" : "text-gray-500"}`} />}
              iconBg={sendHeaders ? "bg-blue-100" : "bg-gray-200"}
              enabled={sendHeaders}
              onToggle={() => setSendHeaders(!sendHeaders)}
            >
              <KeyValueList
                items={headers}
                setItems={setHeaders}
                addParam={addParam}
                removeParam={removeParam}
                updateParam={updateParam}
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                keyPlaceholder="Header-Name"
                valuePlaceholder="value"
                addButtonText="Add Header"
              />
            </CollapsibleSection>

            <CollapsibleSection
              title="Body"
              icon={<Send className={`w-4 h-4 ${sendBody ? "text-emerald-600" : "text-gray-500"}`} />}
              iconBg={sendBody ? "bg-emerald-100" : "bg-gray-200"}
              enabled={sendBody}
              onToggle={() => setSendBody(!sendBody)}
            >
              {/* Body Type Toggle: Key-Value vs JSON */}
              <div className="flex items-center gap-2 mb-3">
                <button
                  onClick={() => setIsJsonBody(false)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    !isJsonBody
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <GripVertical className="w-3.5 h-3.5" />
                  Key-Value
                </button>
                <button
                  onClick={() => setIsJsonBody(true)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                    isJsonBody
                      ? "bg-gray-900 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  <Braces className="w-3.5 h-3.5" />
                  JSON
                </button>
              </div>

              {isJsonBody ? (
                <JsonBodyEditor
                  value={jsonBody}
                  onChange={setJsonBody}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                />
              ) : (
                <KeyValueList
                  items={bodyParams}
                  setItems={setBodyParams}
                  addParam={addParam}
                  removeParam={removeParam}
                  updateParam={updateParam}
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  keyPlaceholder="key"
                  valuePlaceholder="value"
                  addButtonText="Add Body Field"
                />
              )}
            </CollapsibleSection>
          </div>
        </div>
      </div>
    </WorkflowNodeModal>
  );
};

// Collapsible Section Component
interface CollapsibleSectionProps {
  title: string;
  description?: string;
  icon: React.ReactNode;
  iconBg: string;
  enabled: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  description,
  icon,
  iconBg,
  enabled,
  onToggle,
  children,
}) => (
  <div className="bg-[#fbfcfd] rounded-lg border border-gray-200 overflow-hidden">
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors"
    >
      <div className="flex items-center gap-3">
        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${iconBg}`}>
          {icon}
        </div>
        <div className="text-left">
          <span className="text-sm font-semibold text-gray-800 block">{title}</span>
          {description ? <span className="text-xs text-gray-500">{description}</span> : null}
        </div>
      </div>
      <div className={`w-10 h-5 rounded-full transition-colors flex items-center px-0.5 ${enabled ? "bg-gray-900" : "bg-gray-300"}`}>
        <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform ${enabled ? "translate-x-5" : "translate-x-0"}`} />
      </div>
    </button>

    {enabled && (
      <div className="px-4 pb-4 animate-fadeIn">
        {children}
      </div>
    )}
  </div>
);

// JSON Body Editor Component
interface JsonBodyEditorProps {
  value: string;
  onChange: (value: string) => void;
  onDrop: (e: React.DragEvent, setter: (value: string) => void, currentValue: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
}

const JsonBodyEditor: React.FC<JsonBodyEditorProps> = ({
  value,
  onChange
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);

  const validateJson = (val: string) => {
    if (!val.trim()) {
      setJsonError(null);
      return;
    }
    try {
      JSON.parse(val);
      setJsonError(null);
    } catch (e: any) {
      setJsonError(e.message);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    onChange(newValue);
    validateJson(newValue);
  };

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(value);
      const formatted = JSON.stringify(parsed, null, 2);
      onChange(formatted);
      setJsonError(null);
    } catch {
      // can't format invalid JSON
    }
  };

  const handleLocalDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const variableString = e.dataTransfer.getData("text/plain");
    if (variableString && textareaRef.current) {
      const textarea = textareaRef.current;
      const startPos = textarea.selectionStart || value.length;
      const endPos = textarea.selectionEnd || value.length;
      const newValue =
        value.substring(0, startPos) +
        variableString +
        value.substring(endPos);
      onChange(newValue);
      validateJson(newValue);
    }
  };

  const handleLocalDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleLocalDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.max(200, textareaRef.current.scrollHeight) + "px";
    }
  }, [value]);

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center gap-2">
          <Code className="w-3.5 h-3.5 text-gray-500" />
          <span className="text-xs font-medium text-gray-600">JSON Body</span>
        </div>
        <div className="flex items-center gap-2">
          {jsonError ? (
            <span className="text-[10px] text-red-500 font-medium flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              Invalid JSON
            </span>
          ) : value.trim() ? (
            <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
              <CheckCircle className="w-3 h-3" />
              Valid
            </span>
          ) : null}
          <button
            onClick={handleFormat}
            className="text-[10px] font-medium text-gray-500 hover:text-gray-700 px-2 py-1 rounded hover:bg-gray-100 transition-colors"
          >
            Format
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        className={`relative ${isDragOver ? "ring-2 ring-inset ring-gray-900" : ""}`}
        onDrop={handleLocalDrop}
        onDragOver={handleLocalDragOver}
        onDragLeave={handleLocalDragLeave}
      >
        <textarea
          ref={textareaRef}
          value={value}
          onChange={handleChange}
          spellCheck={false}
          className={`w-full px-4 py-3 text-sm font-mono text-gray-800 bg-white resize-none outline-none leading-relaxed ${
            jsonError ? "bg-red-50/30" : ""
          }`}
          style={{ minHeight: "200px", tabSize: 2 }}
          placeholder='{\n  "key": "value"\n}'
          onKeyDown={(e) => {
            // Handle Tab key for indentation
            if (e.key === "Tab") {
              e.preventDefault();
              const textarea = e.currentTarget;
              const start = textarea.selectionStart;
              const end = textarea.selectionEnd;
              const newValue = value.substring(0, start) + "  " + value.substring(end);
              onChange(newValue);
              // Set cursor position after the tab
              setTimeout(() => {
                textarea.selectionStart = textarea.selectionEnd = start + 2;
              }, 0);
            }
          }}
        />
        {isDragOver && (
          <div className="absolute inset-0 bg-gray-900/5 flex items-center justify-center pointer-events-none">
            <div className="bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200">
              <span className="text-xs text-gray-700 font-semibold">Drop variable here</span>
            </div>
          </div>
        )}
      </div>

      {/* Help text */}
      <div className="px-3 py-2 bg-gray-50 border-t border-gray-100">
        <p className="text-[10px] text-gray-400">
          Use template variables like <code className="bg-gray-200 px-1 rounded text-gray-600">{"{{NodeName.outputs.key}}"}</code> for dynamic values. Drag variables from the left panel.
        </p>
      </div>
    </div>
  );
};

// Key-Value List Component
interface KeyValueListProps {
  items: KeyValueParam[];
  setItems: React.Dispatch<React.SetStateAction<KeyValueParam[]>>;
  addParam: (setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => void;
  removeParam: (id: string, setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => void;
  updateParam: (id: string, field: "key" | "value", newValue: string, setter: React.Dispatch<React.SetStateAction<KeyValueParam[]>>) => void;
  onDrop: (e: React.DragEvent, setter: (value: string) => void, currentValue: string) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: (e: React.DragEvent) => void;
  keyPlaceholder: string;
  valuePlaceholder: string;
  addButtonText: string;
}

const KeyValueList: React.FC<KeyValueListProps> = ({
  items,
  setItems,
  addParam,
  removeParam,
  updateParam,
  onDrop,
  onDragOver,
  onDragLeave,
  keyPlaceholder,
  valuePlaceholder,
  addButtonText,
}) => (
  <div className="bg-white rounded-lg border border-gray-200 p-3 space-y-2">
    {items.map((item) => (
      <div key={item.id} className="flex gap-2 items-center group/row">
        <GripVertical className="w-4 h-4 text-gray-300 cursor-grab flex-shrink-0" />
        <input
          type="text"
          value={item.key}
          onChange={(e) => updateParam(item.id, "key", e.target.value, setItems)}
          placeholder={keyPlaceholder}
          className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm focus:border-gray-900 focus:ring-0 outline-none font-mono"
        />
        <div
          onDrop={(e) => onDrop(e, (val) => updateParam(item.id, "value", val, setItems), item.value)}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className="flex-1"
        >
          <input
            type="text"
            value={item.value}
            onChange={(e) => updateParam(item.id, "value", e.target.value, setItems)}
            placeholder={valuePlaceholder}
            className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm focus:border-gray-900 focus:ring-0 outline-none font-mono"
          />
        </div>
        <button
          onClick={() => removeParam(item.id, setItems)}
          className="p-1.5 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors opacity-0 group-hover/row:opacity-100 flex-shrink-0"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    ))}
    <button
      onClick={() => addParam(setItems)}
      className="w-full py-2 text-xs font-medium text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-md border border-dashed border-gray-300 flex items-center justify-center gap-1.5 transition-colors"
    >
      <Plus className="w-3.5 h-3.5" /> {addButtonText}
    </button>
  </div>
);

export default HttpApiConfigModal;
